<template>
    <div>
        <Head :title="title" />

        <jet-banner />

        <div class="flex flex-col min-h-screen theme_color">
            <nav class="border-b border-dark-gray-100">
                <!-- Primary Navigation Menu -->
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="flex justify-between h-24">
                        <div class="flex">
                            <!-- Logo -->
                            <div class="flex-shrink-0 flex items-center">
                                <Link :href="route('home')">
                                    <jet-application-mark class="block h-20 w-auto" />
                                </Link>
                            </div>

                            <!-- Navigation Links -->
                            <div v-if="$page.props.user" class="hidden space-x-8 sm:-my-px sm:ml-10 sm:flex">
                                <jet-nav-link v-if="$page.props.user.role === 'applicant'" :href="route('applicant.dashboard')" :active="route().current('applications.*')">
                                    My Rental Applications
                                </jet-nav-link>
                            </div>

                            <div v-if="$page.props.user" class="hidden space-x-8 sm:-my-px sm:ml-10 sm:flex">
                                <jet-nav-link v-if="$page.props.user.role === 'agent'" :href="route('properties.index')" :active="route().current('properties.*')">
                                    Properties
                                </jet-nav-link>
                            </div>
                        </div>
                        <div v-if="$page.props.user" class="hidden sm:flex sm:items-center sm:ml-6">
                            <!-- Settings Dropdown -->
                            <div class="ml-3 relative">
                                <jet-dropdown align="right" width="48">
                                    <template #trigger>
                                        <button v-if="$page.props.jetstream.managesProfilePhotos" class="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:border-gray-300 transition">
                                            <img class="h-8 w-8 rounded-full object-cover" :src="$page.props.user.profile_photo_url" :alt="$page.props.user.name" />
                                        </button>

                                        <span v-else class="inline-flex rounded-md">
                                            <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-7 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition">
                                                {{ $page.props.user.name }}

                                                <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                                </svg>
                                            </button>
                                        </span>
                                    </template>

                                    <template #content>
                                        <!-- Account Management -->
                                        <div class="block px-4 py-2 text-xs text-gray-400">
                                            Manage Account
                                        </div>

                                        <jet-dropdown-link :href="route('profile.show')">
                                            Profile
                                        </jet-dropdown-link>

                                        <jet-dropdown-link :href="route('api-tokens.index')" v-if="$page.props.jetstream.hasApiFeatures">
                                            API Tokens
                                        </jet-dropdown-link>

                                        <div class="border-t border-gray-100"></div>

                                        <!-- Authentication -->
                                        <form @submit.prevent="logout">
                                            <jet-dropdown-link as="button">
                                                Log Out
                                            </jet-dropdown-link>
                                        </form>
                                    </template>
                                </jet-dropdown>
                            </div>
                        </div>

                        <!-- Hamburger -->
                        <div class="-mr-2 flex items-center sm:hidden">
                            <button @click="showingNavigationDropdown = ! showingNavigationDropdown" class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition">
                                <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                    <path :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                                    <path :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Responsive Navigation Menu -->
                <div v-if="$page.props.user" :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="sm:hidden">
                    <div v-if="$page.props.user.role === 'applicant'" class="pt-2 pb-3 space-y-1">
                        <jet-responsive-nav-link :href="route('applicant.dashboard')" :active="route().current('home')">
                            My Rental Applications
                        </jet-responsive-nav-link>
                    </div>

                    <div v-if="$page.props.user" class="pt-2 pb-3 space-y-1">
                        <jet-responsive-nav-link v-if="$page.props.user.role === 'agent'" :href="route('properties.index')" :active="route().current('properties.*')">
                            Properties
                        </jet-responsive-nav-link>
                    </div>

                    <!-- Responsive Settings Options -->
                </div>
            </nav>

            <!-- Page Heading -->
<!--            <header class="shadow" v-if="$slots.header">-->
<!--                <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">-->
<!--                    <slot name="header"></slot>-->
<!--                </div>-->
<!--            </header>-->

            <!-- Page Content -->
            <main class="flex-1">
                <slot></slot>
            </main>

            <footer>
                <div class="max-w-7xl mx-auto py-4 px-4 sm:px-6 md:flex md:items-center md:justify-between lg:px-8">
                    <div class="flex justify-center space-x-6 md:order-3 md:mt-6">
                        <div class="flex justify-right justify-end justify-around flex-col text-sm text-gray-300">
                            <div class="flex flex-row text-purple-400 gap-2">
                                <a href="tel:5403648271" class="text-purple-400 hover:text-gray-300 hover:border-golden-100 border-b-2 border-transparent pt-1">Call Us</a>
                                <inertia-link :href="route('privacy-policy')" class="text-purple-400 hover:text-gray-300 hover:border-golden-100 border-b-2 border-transparent pt-1">Privacy Policy</inertia-link>
                                <inertia-link :href="route('terms-and-conditions')" class="text-purple-400 hover:text-gray-300 hover:border-golden-100 border-b-2 border-transparent pt-1">Terms and Conditions</inertia-link>
                                <inertia-link :href="route('dispute')" class="text-purple-400 hover:text-gray-300 hover:border-golden-100 border-b-2 border-transparent pt-1">Dispute</inertia-link>
                            </div>
                        </div>
                    </div>
                    <div class="mt-8 md:mt-0 md:order-2">
                        <img class="block h-24 w-auto" :src="logo" />
                    </div>
                    <div class="mt-8 md:mt-0 md:order-1 md:pt-6">
                        <p class="text-center text-base text-purple-400">
                            &copy; {{currentYear}} RentRisk, LLC. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    </div>
</template>

<script>
    import JetApplicationMark from '@/Jetstream/ApplicationMark.vue'
    import JetBanner from '@/Jetstream/Banner.vue'
    import JetDropdown from '@/Jetstream/Dropdown.vue'
    import JetDropdownLink from '@/Jetstream/DropdownLink.vue'
    import JetNavLink from '@/Jetstream/NavLink.vue'
    import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink.vue'
    import TailwindLayout from '@/Layouts/TailwindLayout.vue'
    import { Head, Link } from '@inertiajs/vue3';
    import logo from '../../images/rentrisk-logo.png';

    export default {
        props: {
            title: String,
        },

        components: {
            Head,
            JetApplicationMark,
            JetBanner,
            JetDropdown,
            JetDropdownLink,
            JetNavLink,
            JetResponsiveNavLink,
            Link,
            TailwindLayout,
            logo
        },

        data() {
            return {
                showingNavigationDropdown: false,
            }
        },

        methods: {
            switchToTeam(team) {
                this.$inertia.put(route('current-team.update'), {
                    'team_id': team.id
                }, {
                    preserveState: false
                })
            },

            logout() {
                this.$inertia.post(route('logout'));
            },
        }
    }
</script>
